import { useMemo, useState, type ReactNode } from 'react'
import { Check, ChevronsUpDown, Loader2, Search } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

/** Toute option affichable dans le shell : une valeur + un texte de recherche pré-calculé. */
export interface SearchableComboboxOption {
  value: string
  searchText: string
}

interface SearchableComboboxShellProps<T extends SearchableComboboxOption> {
  options: T[]
  selectedValue: string | null
  onSelect: (value: string) => void
  /** Contenu de chaque ligne d'option (hors wrapper `flex-1` et coche de sélection). */
  renderOption: (option: T, isSelected: boolean) => ReactNode
  /** Contenu du bouton déclencheur (libellé + badge éventuel). */
  triggerContent: ReactNode
  /** true si rien n'est sélectionné → texte du déclencheur atténué. */
  isPlaceholder: boolean
  searchPlaceholder: string
  disabled?: boolean
  error?: boolean
  className?: string
  isLoading?: boolean
  /** Message d'attente (avec spinner) ; défaut : rien de spécial. */
  loadingMessage?: ReactNode
  /** Message quand la liste est vide (avant recherche). */
  emptyMessage: ReactNode
  /** Message quand la recherche ne renvoie rien. */
  noResultsMessage: ReactNode
  /** Pied optionnel (ex. lien « créer un contact ») ; reçoit une fonction pour fermer le menu. */
  renderFooter?: (close: () => void) => ReactNode
}

function ComboboxMessage({ children, loading }: { children: ReactNode; loading?: boolean }) {
  return (
    <div className={cn('px-2 py-1.5 text-sm text-muted-foreground', loading && 'flex items-center gap-2')}>
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </div>
  )
}

/**
 * Coquille réutilisable de combobox searchable (Popover + champ de recherche + liste scrollable +
 * lignes sélectionnables au clavier/souris). Factorise le shell commun aux sélecteurs portage
 * (société, contact, référent questionnaire) ; le contenu du déclencheur et des options est injecté
 * via render-props. La recherche et l'état ouvert/fermé sont gérés en interne.
 */
export function SearchableComboboxShell<T extends SearchableComboboxOption>({
  options,
  selectedValue,
  onSelect,
  renderOption,
  triggerContent,
  isPlaceholder,
  searchPlaceholder,
  disabled = false,
  error = false,
  className,
  isLoading = false,
  loadingMessage,
  emptyMessage,
  noResultsMessage,
  renderFooter,
}: SearchableComboboxShellProps<T>) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const filteredOptions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return options
    return options.filter((opt) => opt.searchText.includes(q))
  }, [options, searchQuery])

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setSearchQuery('')
    setOpen(nextOpen)
  }

  const handleSelect = (optionValue: string) => {
    onSelect(optionValue)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between min-h-9 font-normal',
            isPlaceholder && 'text-muted-foreground',
            error && 'border-red-500',
            className
          )}
          disabled={disabled}
        >
          {triggerContent}
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-(--radix-popover-trigger-width) min-w-[280px] max-w-[400px] p-0 z-21474831000"
        align="start"
      >
        <div className="p-2 border-b bg-muted/30">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="pl-8 h-8 bg-background"
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
        </div>
        <div
          className="max-h-[260px] overflow-y-auto overflow-x-hidden overscroll-contain p-1 touch-pan-y isolate"
          onWheel={(e) => e.stopPropagation()}
          role="listbox"
        >
          {isLoading ? (
            <ComboboxMessage loading>{loadingMessage}</ComboboxMessage>
          ) : options.length === 0 ? (
            <ComboboxMessage>{emptyMessage}</ComboboxMessage>
          ) : filteredOptions.length === 0 ? (
            <ComboboxMessage>{noResultsMessage}</ComboboxMessage>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = selectedValue === option.value
              return (
                <div
                  key={option.value}
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={0}
                  className={cn(
                    'flex items-center gap-2 rounded-sm px-2 py-1.5 cursor-pointer hover:bg-accent',
                    isSelected && 'bg-accent'
                  )}
                  onClick={() => handleSelect(option.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleSelect(option.value)
                    }
                  }}
                >
                  <div className="flex-1 min-w-0">{renderOption(option, isSelected)}</div>
                  {isSelected && <Check className="h-4 w-4 shrink-0 text-primary" />}
                </div>
              )
            })
          )}
        </div>
        {renderFooter?.(() => setOpen(false))}
      </PopoverContent>
    </Popover>
  )
}
